import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import ArticleIcon from '@mui/icons-material/Article'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import LaunchIcon from '@mui/icons-material/Launch'

import { AdminPaperModalStyle } from '../styles'
import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'
import { validateField, validateOptionalField, descriptionLimit, validateRequiredResourceTags, collectTagData } from '../../utils/formValidation'
import ResourceTagsSection from '../shared/ResourceTagsSection'
import ResourceCheckboxes from '../shared/ResourceCheckboxes'
import TCAIREMFieldsSection from '../shared/TCAIREMFieldsSection'
import CoreResourceFields from '../shared/CoreResourceFields'
import ResourceDescriptionField from '../shared/ResourceDescriptionField'

import { setErrorNotification } from '../../reducers/notificationReducer'
import { deletePaper, updatePaper } from '../../reducers/papersReducer'
import { openExternal } from '../../utils/safeUrl'

const AdminPaperModal = (props) => {
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    // form state (no need to use redux for form state)
    const [name, setName] = useState('')
    const [nameError, setNameError] = useState(false)
    const [nameHelper, setNameHelper] = useState('')
    const [authors, setAuthors] = useState('')
    const [authorsError, setAuthorsError] = useState(false)
    const [authorsHelper, setAuthorsHelper] = useState('')
    const [link, setLink] = useState('')
    const [linkError, setLinkError] = useState(false)
    const [linkHelper, setLinkHelper] = useState('')
    const [date, setDate] = useState('')
    const [dateError, setDateError] = useState(false)
    const [dateHelper, setDateHelper] = useState('')
    const [doi, setDoi] = useState('')
    const [doiError, setDoiError] = useState(false)
    const [doiHelper, setDoiHelper] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')
    const [contentTags, setContentTags] = useState([])
    const [medicalTags, setMedicalTags] = useState([])
    const [adminCategoryTags, setAdminCategoryTags] = useState([])
    const [experienceTags, setExperienceTags] = useState([])
    const [demographicTags, setDemographicTags] = useState([])
    const [showTagErrors, setShowTagErrors] = useState(false)
    const [volume, setVolume] = useState('')
    const [journal, setJournal] = useState('')
    const [pages, setPages] = useState('')
    const [issue, setIssue] = useState('')
    const [abstract, setAbstract] = useState('')
    const [isSeminalWork, setIsSeminalWork] = useState(false)
    const [inQuiz, setInQuiz] = useState(false)
    const [isTcairem, setIsTcairem] = useState(false)
    const [isPaid, setIsPaid] = useState(false)
    const [tcairemTags, setTcairemTags] = useState([])
    const [personaTags, setPersonaTags] = useState([])

    // init fields
    useEffect(() => {
        setName(props.title)
        setAuthors(props.authors)
        setLink(props.link)
        setDate(props.pub_date)
        setDoi(props.doi)
        setDescription(props.description || '')
        setContentTags((props.content_tags || props.tags || []).map(String))
        setMedicalTags((props.medical_specialty_tags || []).map(String))
        setAdminCategoryTags((props.admin_category_tags || []).map(String))
        setExperienceTags((props.experience_tags || []).map(String))
        setDemographicTags((props.demographic_tags || []).map(String))
        setIsSeminalWork(props.is_seminal_work || false)
        setInQuiz(props.in_quiz || false)
        setIsTcairem(props.is_tcairem || false)
        setIsPaid(props.is_paid || false)
        setTcairemTags((props.tcairem_type_tags || []).map(String))
        setPersonaTags((props.tcairem_persona_tags || []).map(String))

        if(props.volume) setVolume(props.volume)

        if(props.journal) setJournal(props.journal)
        if(props.pages) setPages(props.pages)
        if(props.issue) setIssue(props.issue)
        if(props.abstract) setAbstract(props.abstract)
    }, [props.title, props.authors, props.link, props.pub_date, props.doi, props.description, props.content_tags, props.medical_specialty_tags, props.admin_category_tags, props.experience_tags, props.demographic_tags, props.tags, props.volume, props.journal, props.pages, props.issue, props.abstract, props.is_seminal_work, props.in_quiz, props.is_tcairem, props.is_paid, props.tcairem_type_tags, props.tcairem_persona_tags])

    // reset fields to original values if user wants to
    const reset = () => {
        setName(props.title)
        setAuthors(props.authors)
        setLink(props.link)
        setDate(props.pub_date)
        setDoi(props.doi)
        setDescription(props.description || '')
        setContentTags((props.content_tags || props.tags || []).map(String))
        setMedicalTags((props.medical_specialty_tags || []).map(String))
        setAdminCategoryTags((props.admin_category_tags || []).map(String))
        setExperienceTags((props.experience_tags || []).map(String))
        setDemographicTags((props.demographic_tags || []).map(String))
        setIsSeminalWork(props.is_seminal_work || false)
        setInQuiz(props.in_quiz || false)
        setIsTcairem(props.is_tcairem || false)
        setIsPaid(props.is_paid || false)
        setTcairemTags((props.tcairem_type_tags || []).map(String))
        setPersonaTags((props.tcairem_persona_tags || []).map(String))

        if(props.volume) setVolume(props.volume)

        else setVolume('')

        if(props.journal) setJournal(props.journal)
        else setJournal('')

        if(props.pages) setPages(props.pages)
        else setPages('')

        if(props.issue) setIssue(props.issue)
        else setIssue('')

        if(props.abstract) setAbstract(props.abstract)
        else setAbstract('')

        setNameError(false)
        setNameHelper('')
        setAuthorsError(false)
        setAuthorsHelper('')
        setLinkError(false)
        setLinkHelper('')
        setDateError(false)
        setDateHelper('')
        setDoiError(false)
        setDoiHelper('')
        setDescriptionError(false)
        setDescriptionHelper('')
        setShowTagErrors(false)
    }

    // delete resource
    const handleReject = async () => {
        if (window.confirm('Are you sure? Note this resource cannot be retrieved after rejection.')) {
            handleClose()
            await dispatch(deletePaper(props.id, user.staff))
            props.onChanged?.()
        }
    }

    // if user clicks approve/disapprove all I need to do is toggle approved state and update fields
    const handleSubmit = async () => {
        setShowTagErrors(true)
        if (
            validateField(name, setNameError, setNameHelper) &&
            validateField(authors, setAuthorsError, setAuthorsHelper) &&
            validateField(link, setLinkError, setLinkHelper) &&
            validateField(date, setDateError, setDateHelper) &&
            validateField(doi, setDoiError, setDoiHelper) &&
            validateOptionalField(description, setDescriptionError, setDescriptionHelper, descriptionLimit(props.description), 'Description') &&
            validateRequiredResourceTags({
                contentTags,
                adminCategoryTags,
                experienceTags,
                demographicTags,
                tcairemTags,
                personaTags,
                isTcairem,
            })
        ) {
            if (window.confirm('Are you sure? Note any changes you have made will be included.')) {
                const data = {
                    ...props,
                    description: description,
                    title: name,
                    authors: authors,
                    link: link,
                    pub_date: date,
                    doi: doi,
                    approved: !props.approved,
                    is_seminal_work: isSeminalWork,
                    in_quiz: inQuiz,
                    is_tcairem: isTcairem,
                    is_paid: isPaid,
                    ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
                }

                if(volume) data.volume = volume

                if(journal) data.journal = journal
                if(pages) data.pages = pages
                if(issue) data.issue = issue
                if(abstract) data.abstract = abstract

                handleClose()
                await dispatch(updatePaper(props.id, data, user.staff))
                props.onChanged?.()
            }
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    // do not change approved state just update fields
    const handleSave = async () => {
        setShowTagErrors(true)
        if (
            validateField(name, setNameError, setNameHelper) &&
            validateField(authors, setAuthorsError, setAuthorsHelper) &&
            validateField(link, setLinkError, setLinkHelper) &&
            validateField(date, setDateError, setDateHelper) &&
            validateField(doi, setDoiError, setDoiHelper) &&
            validateOptionalField(description, setDescriptionError, setDescriptionHelper, descriptionLimit(props.description), 'Description') &&
            validateRequiredResourceTags({
                contentTags,
                adminCategoryTags,
                experienceTags,
                demographicTags,
                tcairemTags,
                personaTags,
                isTcairem,
            })
        ) {
            const data = {
                description: description,
                title: name,
                authors: authors,
                link: link,
                pub_date: date,
                doi: doi,
                is_seminal_work: isSeminalWork,
                in_quiz: inQuiz,
                is_tcairem: isTcairem,
                is_paid: isPaid,
                ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
            }

            if(volume) data.volume = volume

            if(journal) data.journal = journal
            if(pages) data.pages = pages
            if(issue) data.issue = issue
            if(abstract) data.abstract = abstract

            const ok = await dispatch(updatePaper(props.id, data, user.staff))
            if (ok) {
                handleClose()
                props.onChanged?.()
            }
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    const [open, setOpen] = useState(false)

    const handleClickOpen = () => {
        setShowTagErrors(false)
        setOpen(true)
    }

    const handleClose = () => {
        setShowTagErrors(false)
        setOpen(false)
    }

    const openResource = () => {
        openExternal(props.link)
        return false;
    }

    return (
        <div>
            <Button {...AdminPaperModalStyle.viewButton.props} onClick={()=>handleClickOpen()} sx={AdminPaperModalStyle.viewButton.style}>
                <EditIcon /> &nbsp; Edit
            </Button>
            <BootstrapDialog
                onClose={()=>handleClose()}
                aria-labelledby={`paper-modal-${props.id}`}
                open={open}
                fullWidth
                {...AdminPaperModalStyle.modal}
            >
                <BootstrapDialogTitle id={`paper-modal-${props.id}`} onClose={()=>handleClose()}>
                    <Box sx={AdminPaperModalStyle.titleContainer}>
                        <ArticleIcon sx={AdminPaperModalStyle.icon} />
                        {props.title}
                    </Box>
                </BootstrapDialogTitle>
                    <DialogContent dividers sx={AdminPaperModalStyle.content}>
                        <form onSubmit={handleSubmit} id={`paper-form-${props.id}`}>
                            <Typography {...AdminPaperModalStyle.subheading} gutterBottom>
                                Manage Resource
                            </Typography>
                            {props.proposer && <Typography {...AdminPaperModalStyle.proposerText}>
                                Created By: {props.proposer.name} ({props.proposer.email})
                            </Typography>}
                            <Grid container spacing={2} sx={{marginTop: '10px'}}>
                                <CoreResourceFields
                                    name={name} setName={setName} nameError={nameError} setNameError={setNameError} nameHelper={nameHelper} setNameHelper={setNameHelper}
                                    authors={authors} setAuthors={setAuthors} authorsError={authorsError} setAuthorsError={setAuthorsError} authorsHelper={authorsHelper} setAuthorsHelper={setAuthorsHelper}
                                    link={link} setLink={setLink} linkError={linkError} setLinkError={setLinkError} linkHelper={linkHelper} setLinkHelper={setLinkHelper}
                                    date={date} setDate={setDate} dateError={dateError} setDateError={setDateError} dateHelper={dateHelper} setDateHelper={setDateHelper}
                                    validate={validateField}
                                />
                                <ResourceDescriptionField
                                    value={description} setValue={setDescription}
                                    error={descriptionError} setError={setDescriptionError}
                                    helper={descriptionHelper} setHelper={setDescriptionHelper}
                                    limit={descriptionLimit(props.description)}
                                />
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        value={doi}
                                        onChange={e => setDoi(e.target.value)}
                                        label="DOI"
                                        required
                                        error={doiError}
                                        helperText={doiHelper}
                                        onBlur={()=>validateField(doi, setDoiError, setDoiHelper)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        value={volume}
                                        onChange={e => setVolume(e.target.value)}
                                        label="Volume"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        value={issue}
                                        onChange={e => setIssue(e.target.value)}
                                        label="Issue"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        value={journal}
                                        onChange={e => setJournal(e.target.value)}
                                        label="Journal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        value={pages}
                                        onChange={e => setPages(e.target.value)}
                                        label="Pages"
                                        placeholder='Suggested Page Range'
                                    />
                                </Grid>
                                <ResourceTagsSection
                                    contentTags={contentTags} setContentTags={setContentTags}
                                    medicalTags={medicalTags} setMedicalTags={setMedicalTags}
                                    adminCategoryTags={adminCategoryTags} setAdminCategoryTags={setAdminCategoryTags}
                                    experienceTags={experienceTags} setExperienceTags={setExperienceTags}
                            demographicTags={demographicTags} setDemographicTags={setDemographicTags}
                                    required={true}
                                    forceShowRequiredErrors={showTagErrors}
                                />
                                <ResourceCheckboxes
                                    inQuiz={inQuiz} setInQuiz={setInQuiz}
                                    isSeminalWork={isSeminalWork} setIsSeminalWork={setIsSeminalWork}
                                />
                                <TCAIREMFieldsSection
                                    isTcairem={isTcairem} setIsTcairem={setIsTcairem}
                                    isPaid={isPaid} setIsPaid={setIsPaid}
                                    tcairemTags={tcairemTags} setTcairemTags={setTcairemTags}
                                    personaTags={personaTags} setPersonaTags={setPersonaTags}
                                    forceShowRequiredErrors={showTagErrors}
                                />
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        value={abstract}
                                        onChange={e => setAbstract(e.target.value)}
                                        label="Abstract"
                                        multiline
                                        rows={6}
                                        placeholder="Enter the paper abstract (optional)"
                                    />
                                </Grid>
                            </Grid>
                            <Button {...AdminPaperModalStyle.resetButton} type='button' onClick={() => reset()} sx={{marginTop: '20px'}}>
                                Reset Fields
                            </Button>
                        </form>
                    </DialogContent>
                    <DialogActions>
                        <Button type='button' onClick={()=>openResource()}>
                            View Resource&nbsp;<LaunchIcon {...AdminPaperModalStyle.launch}/>
                        </Button>
                        <Button type='button' onClick={()=>handleSave()}>
                            Save Changes
                        </Button>
                        <Button type='submit' form={`paper-form-${props.id}`}>
                            {props.approved? 'Disapprove':'Approve'}
                        </Button>
                        <Button type='button' onClick={()=>handleReject()}>
                            {props.approved? 'Delete':'Reject'}
                        </Button>
                    </DialogActions>
            </BootstrapDialog>
        </div>
    )
}

export default AdminPaperModal
